<template>
  <div class="documents-upload-section">
    <!-- Uploaded Files List -->
    <div v-if="uploadedFiles.length > 0" class="mb-3">
      <div class="list-group list-group-flush">
        <div
          v-for="file in uploadedFiles"
          :key="file.id"
          class="list-group-item d-flex justify-content-between align-items-center px-0"
        >
          <div class="d-flex align-items-center flex-grow-1">
            <FontAwesomeIcon icon="file-pdf" class="text-danger me-2" />
            <button
              type="button"
              class="btn btn-link text-decoration-none p-0 text-start text-truncate flex-grow-1"
              @click="downloadFile(file.id)"
              :title="$t('common.download')"
            >
              {{ file.name || `Document ${file.id}` }}
            </button>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-link text-danger p-0 ms-2"
            @click="removeUploadedFile(file.id)"
            :title="$t('common.remove')"
          >
            <FontAwesomeIcon icon="times-circle" />
          </button>
        </div>
      </div>
    </div>

    <!-- Chat assistant: uploads a PPA and/or answers follow-up questions until complete -->
    <AiChatCard
      :companyId="props.companyId"
      endpoint="/api/portfolio/ppa/chat"
      :greeting="$t('ai_chat.greetings.ppa')"
      @analysis-complete="handleChatResult"
    />
  </div>
</template>

<script setup lang="ts">
import { useFileDownload } from "@/composables/useFileDownload";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import AiChatCard from "@/dashboard/inventory_management/common/AiChatCard.vue";
import type { ActionInstructions, PpaAnalyzeUploadResponse } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { ref, watch } from "vue";

const props = defineProps<{
  companyId: number;
}>();

const actionInstructions = defineModel<ActionInstructions>({ required: true });

const emit = defineEmits<{
  "analysis-complete": [analysisResult: any, portfolioInstructionsPatch?: any];
}>();
const { globalErrorHandler } = useGlobalHandlers();
const { downloadCompanyDocument } = useFileDownload();

const uploadedFiles = ref<Array<{ id: number; name?: string }>>([]);

// Each chat turn returns the full draft (same shape as the legacy analyze-upload response).
// Unlike the old one-shot upload, a turn may carry no new document (a text answer), so upload
// ids are synced rather than required.
const handleChatResult = (data: unknown) => {
  const result = data as PpaAnalyzeUploadResponse | null;
  if (!result) {
    return;
  }

  try {
    const extractedData = result.instructions_patch || {};
    const uploadIds = (extractedData.custom_user_upload_ids || []).filter(
      (id: number) => id && id > 0
    );

    if (uploadIds.length > 0) {
      const currentUploadIds =
        actionInstructions.value.custom_user_upload_ids || [];
      const merged = [...new Set([...currentUploadIds, ...uploadIds])];
      actionInstructions.value = {
        ...actionInstructions.value,
        custom_user_upload_ids: merged,
      };
    }

    // Emit for the parent to merge. Either field alone is enough — AI may
    // extract only a limits pattern without filling action-instruction fields.
    const portfolioInstructionsPatch = result.portfolio_instructions_patch;
    const hasExtractedData =
      extractedData && Object.keys(extractedData).length > 0;
    const hasPortfolioInstructionsPatch =
      portfolioInstructionsPatch &&
      Object.keys(portfolioInstructionsPatch).length > 0;

    if (hasExtractedData || hasPortfolioInstructionsPatch) {
      emit("analysis-complete", extractedData, portfolioInstructionsPatch);
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const downloadFile = async (fileId: number) => {
  await downloadCompanyDocument(props.companyId, fileId);
};

const removeUploadedFile = (fileId: number) => {
  uploadedFiles.value = uploadedFiles.value.filter((f) => f.id !== fileId);

  const currentIds = actionInstructions.value.custom_user_upload_ids || [];
  actionInstructions.value = {
    ...actionInstructions.value,
    custom_user_upload_ids: currentIds.filter((id) => id !== fileId),
  };
};

// Watch for changes in action instructions to sync with uploaded files
watch(
  () => actionInstructions.value.custom_user_upload_ids,
  (newIds) => {
    // Update uploadedFiles list to match the IDs
    uploadedFiles.value = (newIds || []).map((id) => ({
      id,
      name:
        uploadedFiles.value.find((f) => f.id === id)?.name || `Document ${id}`,
    }));
  },
  { deep: true, immediate: true }
);
</script>
